import { useCallback, useEffect, useState } from 'react'
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom'
import { getHackWhatWork, voteHackWhatWork } from '@domain/features/pd-hacks/pd-hacks-feature'
import { CommentsOn } from '@domain/models/activity-comments'
import type { WhatWork } from '@domain/models/pd-hacks'
import CommentsSection from '@presentation/components/comments/CommentsSection'
import LoadingSpinner from '@presentation/components/ui/LoadingSpinner'
import { useAuthUser } from '@presentation/hooks/useAuthUser'
import { useHack } from '@presentation/hooks/useHack'
import { useUserProfiles } from '@presentation/hooks/useUserProfiles'
import SeoHead from '@presentation/seo/SeoHead'

export default function HackWhatWorksCommentsPage() {
  const location = useLocation()
  const navigate = useNavigate()
  const { symptomId, whatWorkId } = useParams<{
    symptomId: string
    whatWorkId: string
  }>()
  const { user } = useAuthUser()
  const { hack, loading: hackLoading, error: hackError } = useHack(symptomId)
  const [whatWork, setWhatWork] = useState<WhatWork | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [voteError, setVoteError] = useState<string | null>(null)
  const [voting, setVoting] = useState(false)

  const { resolveAvatarUrl, resolveDisplayName } = useUserProfiles([
    whatWork?.addedById,
  ])

  const loadWhatWork = useCallback(async () => {
    if (!symptomId || !whatWorkId) {
      setError('What works id is missing.')
      setLoading(false)
      return
    }

    try {
      setLoading(true)
      setError(null)
      const result = await getHackWhatWork(symptomId, whatWorkId)
      setWhatWork(result)
    } catch (loadError) {
      setError(
        loadError instanceof Error
          ? loadError.message
          : 'Failed to load what works entry.',
      )
    } finally {
      setLoading(false)
    }
  }, [symptomId, whatWorkId])

  useEffect(() => {
    void loadWhatWork()
  }, [loadWhatWork])

  const openLoginModal = () => {
    const nextParams = new URLSearchParams(location.search)
    nextParams.delete('userProfile')
    nextParams.set('authMode', 'login')

    navigate({
      pathname: location.pathname,
      search: `?${nextParams.toString()}`,
    })
  }

  const handleVote = async (isPositive: boolean) => {
    if (!symptomId || !whatWorkId) {
      return
    }

    if (!user?.uid) {
      openLoginModal()
      return
    }

    try {
      setVoting(true)
      setVoteError(null)
      await voteHackWhatWork(symptomId, whatWorkId, isPositive)
      await loadWhatWork()
    } catch (voteActionError) {
      setVoteError(
        voteActionError instanceof Error
          ? voteActionError.message
          : 'Failed to submit vote.',
      )
    } finally {
      setVoting(false)
    }
  }

  if (hackLoading || loading) {
    return (
      <>
        <SeoHead
          title="Loading discussion | Parkinson's Insights"
          canonicalPath={
            symptomId && whatWorkId
              ? `/hacks/${symptomId}/what-works/${whatWorkId}/comments`
              : '/hacks'
          }
          noindex
        />
        <LoadingSpinner className="min-h-[50vh]" />
      </>
    )
  }

  if (hackError || error || !hack || !whatWork || !symptomId || !whatWorkId) {
    return (
      <>
        <SeoHead
          title="Discussion not found | Parkinson's Insights"
          canonicalPath={
            symptomId && whatWorkId
              ? `/hacks/${symptomId}/what-works/${whatWorkId}/comments`
              : '/hacks'
          }
          noindex
        />
        <div className="flex min-h-[50vh] flex-col items-center justify-center px-4 text-center">
          <p className="text-lg font-semibold text-text-secondary">
            {hackError ?? error ?? 'What works detail not found'}
          </p>
          <Link
            to={symptomId ? `/hacks/${symptomId}` : '/hacks'}
            className="mt-4 text-sm font-semibold text-primary hover:text-primary-light"
          >
            &larr; Back
          </Link>
        </div>
      </>
    )
  }

  const authorName = resolveDisplayName(whatWork.addedById)
  const authorAvatar = resolveAvatarUrl(whatWork.addedById)
  const initials = authorName
    .split(' ')
    .map((part) => part.trim().charAt(0))
    .join('')
    .slice(0, 2)
    .toUpperCase()

  return (
    <article className="bg-background">
      <SeoHead
        title={`${whatWork.solution?.trim() || 'Hack suggestion'} discussion | Michel's PD Hacks`}
        description="Read and join discussion on a community-submitted Parkinson's hack suggestion."
        canonicalPath={`/hacks/${symptomId}/what-works/${whatWorkId}/comments`}
        noindex
      />
      <div className="mx-auto max-w-4xl px-4 py-6 sm:px-6 lg:px-8">
        <Link
          to={`/hacks/${symptomId}`}
          className="inline-flex items-center gap-1 text-sm font-semibold text-primary no-underline hover:text-primary-light"
        >
          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back to Hack
        </Link>

        <section className="mt-5 rounded-xl border border-border-light bg-surface p-4">
          <p className="text-sm font-semibold text-text-secondary">
            {hack.name?.trim() || 'Michel PD Hack'}
          </p>
          <p className="mt-2 text-xl font-bold text-text-primary">
            {whatWork.solution?.trim() || 'Untitled suggestion'}
          </p>

          {whatWork.description?.trim() ? (
            <p className="mt-2 whitespace-pre-wrap text-sm text-text-secondary">
              {whatWork.description}
            </p>
          ) : null}

          <div className="mt-4 flex items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              {authorAvatar ? (
                <img
                  src={authorAvatar}
                  alt={authorName}
                  className="h-9 w-9 rounded-full object-cover"
                />
              ) : (
                <div className="flex h-9 w-9 items-center justify-center rounded-full bg-primary/10 text-xs font-bold text-primary">
                  {initials || 'U'}
                </div>
              )}
              <span className="text-sm font-semibold text-text-primary">{authorName}</span>
            </div>

            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => {
                  void handleVote(true)
                }}
                disabled={voting}
                className={`inline-flex items-center rounded-full px-3 py-2 text-sm font-semibold transition-colors ${
                  whatWork.positiveRatingsIds?.includes(user?.uid ?? '')
                    ? 'bg-blue text-white'
                    : 'bg-border-light text-text-secondary hover:bg-blue/10 hover:text-blue'
                } disabled:cursor-not-allowed disabled:opacity-60`}
              >
                Works
              </button>
              <button
                type="button"
                onClick={() => {
                  void handleVote(false)
                }}
                disabled={voting}
                className={`inline-flex items-center rounded-full px-3 py-2 text-sm font-semibold transition-colors ${
                  whatWork.negativeRatingsIds?.includes(user?.uid ?? '')
                    ? 'bg-red-500 text-white'
                    : 'bg-border-light text-text-secondary hover:bg-red-50 hover:text-red-500'
                } disabled:cursor-not-allowed disabled:opacity-60`}
              >
                Not for me
              </button>
              <span className="inline-flex items-center rounded-full bg-border-light px-3 py-2 text-sm font-semibold text-text-secondary">
                Score: {whatWork.totalVotes ?? 0}
              </span>
            </div>
          </div>
          {voteError ? (
            <p className="mt-2 text-sm font-semibold text-text-secondary">
              {voteError}
            </p>
          ) : null}
        </section>

        <CommentsSection
          targetId={whatWorkId}
          commentsOn={CommentsOn.Symptom}
          symptomId={symptomId}
          onAuthRequired={openLoginModal}
        />
      </div>
    </article>
  )
}
